/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  makeStyles,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core'
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  InfoRounded,
  ErrorOutline as ErrorOutlineIcon
} from '@material-ui/icons'
import ProgressButton from 'components/ProgressButton'
import { parse } from 'papaparse'
import { downloadOrgRepresentativeSampleCSV } from 'utils/downloadCSV'
import { isValidEmail, parseCsvData } from 'utils'
import { bulkCreateRepresentatives, fetchConferenceOrganizations } from 'store/organization'
import { useSelector } from 'react-redux'
import DataLoading from 'components/DataLoading'
import DialogActions from '@material-ui/core/DialogActions'
import { toast } from 'utils/notify'
import ImportSummaryModal from './ImportSummaryModal'

const ImportOrgRepresentativesModal = ({ open, onClose }) => {
  const classes = useStyles()
  const inputFileRef = useRef()
  const [csvFile, setCsvFile] = useState(null)
  const [csvError, setCsvError] = useState('')
  const [csvInfoMsg, setCsvInfoMsg] = useState('')
  const [loading, setLoading] = useState(false)
  const [performingAction, setPerformingAction] = useState(false)
  const [data, setData] = useState([])
  const [invalidData, setInvalidData] = useState([])
  const [invalidOrgData, setInvalidOrgData] = useState([])

  const [confOrganizations, setConfOrganizations] = useState([])
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [importSummary, setImportSummary] = useState({
    added: 0,
    existing: 0,
    invalidOrg: 0,
    invalidData: 0
  })
  const [allRows, setAllRows] = useState([])
  const [duplicateCount, setDuplicateCount] = useState(0)
  const [showReviewTable, setShowReviewTable] = useState(false)

  const { user } = useSelector((state) => state.auth)

  useEffect(() => {
    const fetch = async () => {
      setLoading(true)
      const orgs = await fetchConferenceOrganizations(user?.conferenceId)
      setConfOrganizations(orgs || [])
      setLoading(false)
    }
    open && fetch()
  }, [open])

  const getRowErrors = (row) => {
    const errors = new Set()
    if (!row.firstName) errors.add('firstName')
    if (!row.lastName) errors.add('lastName')
    if (!row.email || !isValidEmail(row.email)) errors.add('email')
    if (!row.organization) errors.add('organization')
    return errors
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) {
      setCsvFile(null)
      return
    }
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setCsvError('Please upload a CSV file')
      setCsvFile(null)
      return
    }
    setCsvError('')
    setCsvFile(file)
    setData([])

    // Parse the CSV file
    parse(file, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: async (results) => {
        if (results.errors.length > 0) {
          setCsvError('Error parsing CSV file')
          return
        }
        const rawRows = parseCsvData(results.data).map((item) => ({
          ...item,
          email: item.email ? item.email.toLowerCase().trim() : item.email
        }))

        // Mark duplicates and categorize rows
        const seenEmails = new Set()
        const validData = []
        const invalidData_ = []
        const invalidOrgData_ = []

        const rows = rawRows.map((rep) => {
          const emailKey = rep.email ? rep.email : null
          const isDuplicate = emailKey ? seenEmails.has(emailKey) : false
          if (emailKey && !isDuplicate) seenEmails.add(emailKey)

          if (isDuplicate)
            return { ...rep, isDuplicate: true, isInvalidFields: false, isInvalidOrg: false }

          const errors = getRowErrors(rep)
          if (errors.size > 0) {
            invalidData_.push(rep)
            return { ...rep, isDuplicate: false, isInvalidFields: true, isInvalidOrg: false }
          }

          const existingOrg =
            confOrganizations.find(
              (org) => org.organization?.toLowerCase() === rep.organization?.toLowerCase()
            ) || null

          if (!existingOrg?.id) {
            invalidOrgData_.push(rep)
            return { ...rep, isDuplicate: false, isInvalidFields: false, isInvalidOrg: true }
          }

          const validRep = {
            ...rep,
            isPrimaryContact: rep?.isPrimaryContact
              ? rep?.isPrimaryContact.toLowerCase() === 'yes'
              : false,
            organizationId: existingOrg?.id,
            isDuplicate: false,
            isInvalidFields: false,
            isInvalidOrg: false
          }
          validData.push(validRep)
          return validRep
        })

        setInvalidData(invalidData_)
        setInvalidOrgData(invalidOrgData_)

        const dupCount = rows.filter((r) => r.isDuplicate).length
        const validCount = validData.length

        if (validCount === 0 && dupCount === 0) {
          let errorMsg = 'All representatives have been rejected due to '
          if (invalidData_.length > 0 && invalidOrgData_.length > 0) {
            errorMsg += 'missing/invalid required fields and invalid organizations.'
          } else if (invalidData_.length > 0) {
            errorMsg += 'missing/invalid required fields.'
          } else {
            errorMsg += 'invalid organizations.'
          }
          setCsvError(errorMsg)
          setShowReviewTable(false)
        } else {
          setData(validData)
          setAllRows(rows)
          setDuplicateCount(dupCount)
          setCsvError('')
          setCsvInfoMsg('')
          setShowReviewTable(true)
        }

        setImportSummary((prev) => ({
          ...prev,
          invalidData: invalidData_.length,
          invalidOrg: invalidOrgData_.length
        }))

        event.target.value = null
      },
      error: (err) => {
        setCsvError(err)
      }
    })
    setCsvError('')
    setCsvInfoMsg('')
    event.target.value = null
  }

  const handleImport = async () => {
    try {
      setPerformingAction(true)

      // Group representatives by organizationId
      const validRows = allRows.filter(
        (r) => !r.isDuplicate && !r.isInvalidFields && !r.isInvalidOrg
      )
      const groupedByOrg = validRows.reduce((acc, rep) => {
        if (!acc[rep.organizationId]) {
          acc[rep.organizationId] = []
        }
        acc[rep.organizationId].push(rep)
        return acc
      }, {})

      // Process each organization's representatives to ensure only one primary contact
      const processedData = Object.entries(groupedByOrg).flatMap(([orgId, orgReps]) => {
        // Find the organization in our fetched list
        const organization = confOrganizations.find((org) => org.id === orgId)

        // Check if the organization already has a primary contact
        if (organization?.primaryContactId) {
          // If organization already has a primary contact, set all reps to non-primary
          return orgReps.map((rep) => ({
            ...rep,
            isPrimaryContact: false
          }))
        }

        // Find all representatives marked as primary contact
        const primaryContacts = orgReps.filter((rep) => rep.isPrimaryContact === true)

        // If more than one primary contact exists, keep only the first one as primary
        if (primaryContacts.length > 1) {
          return orgReps.map((rep) => {
            const isPrimary = rep.isPrimaryContact && rep.email === primaryContacts[0].email

            return {
              ...rep,
              isPrimaryContact: isPrimary
            }
          })
        }

        return orgReps
      })

      const { added, existing } = await bulkCreateRepresentatives(processedData, user?.conferenceId)

      // Prepare summary data for the modal
      setImportSummary({
        added,
        existing,
        invalidOrg: invalidOrgData.length,
        invalidData: invalidData.length
      })

      // Reset the form but don't close the parent modal yet
      setCsvFile(null)
      setData([])
      setAllRows([])
      setDuplicateCount(0)
      setShowReviewTable(false)
      setCsvInfoMsg('')
      setCsvError(null)

      // Close the modal
      onClose()
      // Show the summary modal
      setShowSummaryModal(true)
    } catch (error) {
      console.error('Error importing representatives:', error)
      setCsvError('Error importing representatives')
      toast.error(error.message)
    } finally {
      setPerformingAction(false)
    }
  }

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false)
    setPerformingAction(false)
    onClose() // Close the parent modal
  }

  const handleClose = () => {
    setCsvFile(null)
    setCsvError('')
    setCsvInfoMsg('')
    setData([])
    setAllRows([])
    setDuplicateCount(0)
    setShowReviewTable(false)
    setInvalidData([])
    setInvalidOrgData([])
    onClose()
  }

  return (
    <>
      <Dialog open={open} maxWidth="xl" fullWidth classes={{ paper: classes.dialogPaper }}>
        <Box className={classes.dialogTitle}>
          <Typography variant="h5">Import Representatives from CSV</Typography>
          {!performingAction && (
            <IconButton onClick={handleClose} size="small" className={classes.closeButton}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        <DialogContent className={classes.dialogContent}>
          {loading ? (
            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <DataLoading />
            </div>
          ) : (
            <>
              <Box className={classes.note}>
                <Box className={classes.noteHeader}>
                  <Typography className={classes.noteHead}>
                    How to import Organization representatives:
                  </Typography>
                </Box>

                <Box className={classes.noteSteps}>
                  <Box className={classes.stepItem}>
                    <Box className={classes.stepNumber}>1</Box>
                    <Typography className={classes.stepText}>
                      Upload a CSV file containing organization&apos;s representatives data.
                    </Typography>
                  </Box>

                  <Box className={classes.stepItem}>
                    <Box className={classes.stepNumber}>2</Box>
                    <Typography className={classes.stepText}>
                      Review the data before importing. CSV file can have the following columns:
                    </Typography>
                  </Box>
                  <Box className={classes.fileFormatInfo}>
                    <Box>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '1rem'
                        }}>
                        <Typography variant="subtitle2" className={classes.formatTitle}>
                          Sample CSV Format
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          className={classes.sampleDownloadButton}
                          onClick={downloadOrgRepresentativeSampleCSV}
                          startIcon={<DownloadIcon />}>
                          Download Sample CSV
                        </Button>
                      </Box>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell className={classes.formatHeaderCell}>
                                Organization <span className={classes.required}>*</span>
                              </TableCell>
                              <TableCell className={classes.formatHeaderCell}>
                                First Name <span className={classes.required}>*</span>
                              </TableCell>
                              <TableCell className={classes.formatHeaderCell}>
                                Last Name <span className={classes.required}>*</span>
                              </TableCell>
                              <TableCell className={classes.formatHeaderCell}>
                                Email <span className={classes.required}>*</span>
                              </TableCell>
                              <TableCell className={classes.formatHeaderCell}>
                                Is Primary Contact
                              </TableCell>
                              <TableCell className={classes.formatHeaderCell}>Credential</TableCell>
                              <TableCell className={classes.formatHeaderCell}>Position</TableCell>
                              <TableCell className={classes.formatHeaderCell}>Phone</TableCell>
                              <TableCell className={classes.formatHeaderCell}>City</TableCell>
                              <TableCell className={classes.formatHeaderCell}>State</TableCell>
                              <TableCell className={classes.formatHeaderCell}>Country</TableCell>
                            </TableRow>
                          </TableHead>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>

                  <Box className={classes.stepItem}>
                    <Box className={classes.stepNumber}>3</Box>
                    <Typography className={classes.stepText}>
                      {`After revewing the data, Click "Import Representatives" to add all representatives.`}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box style={{ marginTop: '1rem' }}>
                    <Box mt={3}>
                      <Typography className={classes.emailLabel}>CSV File Uploader</Typography>
                      <input
                        type="file"
                        id="csv-file-input"
                        accept=".csv"
                        onChange={handleFileChange}
                        className={classes.input}
                        ref={inputFileRef}
                        disabled={performingAction}
                      />
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Button
                          variant="outlined"
                          component="span"
                          className={classes.upload}
                          startIcon={<CloudUploadIcon />}
                          onClick={() => inputFileRef.current.click()}
                          disabled={performingAction}>
                          {csvFile ? csvFile.name : 'Select CSV file'}
                        </Button>
                        {csvFile && (
                          <IconButton
                            size="small"
                            title="Clear selected file"
                            disabled={performingAction}
                            onClick={() => {
                              setCsvFile(null)
                              setCsvError('')
                              setCsvInfoMsg('')
                              setData([])
                              setAllRows([])
                              setDuplicateCount(0)
                              setShowReviewTable(false)
                              setInvalidData([])
                              setInvalidOrgData([])
                              if (inputFileRef.current) inputFileRef.current.value = null
                            }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {csvError && (
                        <Box className={classes.errorBox}>
                          <ErrorOutlineIcon className={classes.errorIcon} />
                          <Typography className={classes.error}>{csvError}</Typography>
                        </Box>
                      )}

                      {csvInfoMsg && (
                        <Box className={classes.infoBox}>
                          <InfoIcon className={classes.infoIcon} />
                          <Typography className={classes.info}>{csvInfoMsg}</Typography>
                        </Box>
                      )}
                    </Box>

                    {showReviewTable && allRows.length > 0 && (
                      <Box className={classes.tableContainer}>
                        <Typography variant="h6" className={classes.tableTitle}>
                          Review Representatives ({allRows.length})
                        </Typography>

                        {invalidData.length > 0 && (
                          <Box className={classes.warningBanner}>
                            <InfoRounded className={classes.warningIcon} />
                            <Typography className={classes.warningText}>
                              <strong>
                                {invalidData.length} row{invalidData.length !== 1 ? 's' : ''} have
                                invalid or missing required fields.
                              </strong>{' '}
                              These rows will be <strong>skipped</strong> during import.
                            </Typography>
                          </Box>
                        )}

                        {invalidOrgData.length > 0 && (
                          <Box className={classes.invalidOrgBanner}>
                            <InfoRounded className={classes.invalidOrgIcon} />
                            <Typography className={classes.invalidOrgText}>
                              <strong>
                                {invalidOrgData.length} row
                                {invalidOrgData.length !== 1 ? 's' : ''} reference an organization
                                not found in this conference.
                              </strong>{' '}
                              These rows will be <strong>skipped</strong> during import. Ensure the
                              organization name exactly matches one registered in this conference.
                            </Typography>
                          </Box>
                        )}

                        {duplicateCount > 0 && (
                          <Box className={classes.duplicateBanner}>
                            <InfoRounded className={classes.duplicateIcon} />
                            <Typography className={classes.duplicateText}>
                              <strong>
                                {duplicateCount} duplicate email
                                {duplicateCount !== 1 ? 's' : ''} found.
                              </strong>{' '}
                              Duplicate rows will be <strong>automatically skipped</strong> — only
                              the first occurrence of each email is kept.
                            </Typography>
                          </Box>
                        )}

                        {invalidData.length === 0 &&
                          invalidOrgData.length === 0 &&
                          duplicateCount === 0 && (
                            <Box className={classes.importInfo}>
                              <Box className={classes.infoNumberCircle}>i</Box>
                              <Typography className={classes.importInfoText}>
                                Please review the data before importing. Only representatives with
                                valid information will be imported.
                              </Typography>
                            </Box>
                          )}

                        <TableContainer component={Paper} className={classes.reviewTable}>
                          <Table size="small">
                            <TableHead className={classes.reviewHeader}>
                              <TableRow>
                                <TableCell className={classes.reviewHeaderCell}>
                                  Organization
                                </TableCell>
                                <TableCell className={classes.reviewHeaderCell}>
                                  First Name
                                </TableCell>
                                <TableCell className={classes.reviewHeaderCell}>
                                  Last Name
                                </TableCell>
                                <TableCell className={classes.reviewHeaderCell}>Email</TableCell>
                                <TableCell className={classes.reviewHeaderCell}>
                                  Primary Contact
                                </TableCell>
                                <TableCell className={classes.reviewHeaderCell}>
                                  Credential
                                </TableCell>
                                <TableCell className={classes.reviewHeaderCell}>Position</TableCell>
                                <TableCell className={classes.reviewHeaderCell}>Phone</TableCell>
                                <TableCell className={classes.reviewHeaderCell}>City</TableCell>
                                <TableCell className={classes.reviewHeaderCell}>State</TableCell>
                                <TableCell className={classes.reviewHeaderCell}>Country</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {allRows.map((row, index) => {
                                const errors = row.isInvalidFields ? getRowErrors(row) : new Set()
                                const borderColor = row.isInvalidFields
                                  ? '#f44336'
                                  : row.isInvalidOrg
                                  ? '#f57c00'
                                  : row.isDuplicate
                                  ? '#ff9800'
                                  : 'transparent'
                                return (
                                  <TableRow
                                    key={index}
                                    style={
                                      row.isInvalidFields || row.isInvalidOrg || row.isDuplicate
                                        ? { borderLeft: `3px solid ${borderColor}` }
                                        : {}
                                    }>
                                    <TableCell
                                      className={`${classes.reviewCell} ${
                                        errors.has('organization')
                                          ? classes.cellError
                                          : row.isInvalidOrg
                                          ? classes.cellInvalidOrg
                                          : ''
                                      }`}>
                                      {row.organization || (
                                        <span className={classes.missingValue}>Missing</span>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      className={`${classes.reviewCell} ${
                                        errors.has('firstName') ? classes.cellError : ''
                                      }`}>
                                      {row.firstName || (
                                        <span className={classes.missingValue}>Missing</span>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      className={`${classes.reviewCell} ${
                                        errors.has('lastName') ? classes.cellError : ''
                                      }`}>
                                      {row.lastName || (
                                        <span className={classes.missingValue}>Missing</span>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      className={`${classes.reviewCell} ${
                                        errors.has('email')
                                          ? classes.cellError
                                          : row.isDuplicate
                                          ? classes.cellDuplicate
                                          : ''
                                      }`}>
                                      <Box
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px'
                                        }}>
                                        {row.email || (
                                          <span className={classes.missingValue}>Missing</span>
                                        )}
                                        {row.isDuplicate && (
                                          <span className={classes.duplicateBadge}>Duplicate</span>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell className={classes.reviewCell}>
                                      {row.isDuplicate || row.isInvalidFields || row.isInvalidOrg
                                        ? '-'
                                        : row.isPrimaryContact
                                        ? 'Yes'
                                        : 'No'}
                                    </TableCell>
                                    <TableCell className={classes.reviewCell}>
                                      {row.credential || '-'}
                                    </TableCell>
                                    <TableCell className={classes.reviewCell}>
                                      {row.position || '-'}
                                    </TableCell>
                                    <TableCell className={classes.reviewCell}>
                                      {row.phone || '-'}
                                    </TableCell>
                                    <TableCell className={classes.reviewCell}>
                                      {row.city || '-'}
                                    </TableCell>
                                    <TableCell className={classes.reviewCell}>
                                      {row.state || '-'}
                                    </TableCell>
                                    <TableCell className={classes.reviewCell}>
                                      {row.country || '-'}
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    <Box className={classes.noticeBox} mt={1}>
                      <Typography className={classes.noticeText}>
                        One Organization can have one{' '}
                        <span className={classes.bold}>Primary Contact</span> at a time. If we found
                        more than one primary contacts, we will assign the first one as a primary
                        contact.
                      </Typography>
                    </Box>

              </Box>
            </>
          )}
        </DialogContent>


        <DialogActions className={classes.buttonBox}>
          <Button onClick={handleClose} disabled={performingAction}>
            Cancel
          </Button>
          <ProgressButton
            onClick={handleImport}
            label="Import Representatives"
            disabled={!csvFile || data.length === 0 || performingAction}
            inProgress={performingAction}
            variant="contained"
            color="primary"
          />
        </DialogActions>
      </Dialog>

      <ImportSummaryModal
        open={showSummaryModal}
        onClose={handleCloseSummaryModal}
        summary={importSummary}
      />
    </>
  )
}

const useStyles = makeStyles({
  dialogPaper: {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
  },
  dialogTitle: {
    borderBottom: '1px solid #f0f0f0',
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& h5': {
      fontWeight: 600,
      fontSize: '20px',
      color: '#2c3e50'
    }
  },
  closeButton: {
    color: '#757575'
  },
  dialogContent: {
    padding: '24px 24px 16px'
  },
  modalTitle: {
    letterSpacing: '0.5px',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '16px'
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '1rem',
    borderRadius: '8px'
  },
  rowKey: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#455a64'
  },
  rowValue: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#03a9f4'
  },
  summaryBox: {
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  noticeBox: {
    backgroundColor: 'rgba(3, 169, 244, 0.05)',
    borderLeft: '4px solid #03a9f4',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '10px'
  },
  noticeText: {
    fontSize: '15px',
    letterSpacing: '0.4px',
    lineHeight: '1.6'
  },
  // No duplicated styles
  note: {
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    marginBottom: '24px',
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  noteHeader: {
    marginBottom: '16px'
  },
  noteHead: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#2c3e50',
    lineHeight: 1.5
  },
  noteSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#03a9f4',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
    flexShrink: 0
  },
  stepText: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#37474f',
    lineHeight: 1.6,
    paddingTop: '4px'
  },
  fileFormatInfo: {
    marginLeft: '40px',
    backgroundColor: '#ffffff',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  formatItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    '& > p': {
      fontWeight: 600,
      color: '#455a64',
      marginBottom: '2px'
    }
  },
  formatTags: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  chipTag: {
    backgroundColor: '#e3f2fd',
    color: '#0277bd',
    fontWeight: 500,
    border: '1px solid #90caf9'
  },
  sampleDownloadButton: {
    marginTop: '8px',
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 500,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    borderColor: '#03a9f4',
    color: '#0277bd',
    '&:hover': {
      backgroundColor: '#e3f2fd',
      borderColor: '#0277bd',
      boxShadow: '0 2px 5px rgba(3, 169, 244, 0.15)'
    }
  },
  buttonBox: {
    borderTop: '1px solid #efefef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: '12px',
    padding: ' 16px 24px'
  },
  upload: {
    textTransform: 'none',
    flex: 1,
    justifyContent: 'flex-start',
    padding: '14px 16px',
    fontSize: '14px',
    fontWeight: 500,
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    background: '#FFFFFF',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#03a9f4',
      boxShadow: '0 2px 5px rgba(3, 169, 244, 0.15)'
    }
  },
  optionalChip: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
    fontWeight: 500,
    border: '1px solid #ce93d8'
  },
  input: {
    display: 'none'
  },
  error: {
    marginTop: '0',
    color: '#f44336',
    fontWeight: 500
  },
  infoIcon: {
    marginTop: '0',
    fontSize: '18px',
    color: '#03a9f4'
  },
  infoBox: {
    marginTop: '8px',
    marginLeft: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    borderRadius: '8px',
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  info: {
    marginTop: '0',
    color: '#0288d1',
    fontWeight: 500
  },
  bold: {
    fontWeight: 600
  },
  formatHeaderCell: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #6f6f6f'
  },
  formatTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    color: '#455a64'
  },
  required: {
    fontWeight: 'bold',
    fontSize: '22px',
    color: '#f44336'
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px',
    marginLeft: '12px',
    padding: '8px',
    borderRadius: '8px',
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    border: '1px solid rgba(244, 67, 54, 0.2)'
  },
  errorIcon: {
    fontSize: '18px',
    color: '#f44336',
    marginRight: '8px'
  },
  tableContainer: {
    marginTop: '24px',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: '#ffffff'
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '16px'
  },
  reviewTable: {
    marginBottom: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    borderRadius: '8px',
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    maxHeight: '300px',
    position: 'relative'
  },
  reviewHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#f5f9ff'
  },
  reviewHeaderCell: {
    backgroundColor: '#f5f9ff',
    color: '#0277bd',
    fontWeight: 600,
    padding: '10px 12px',
    fontSize: '13px',
    borderBottom: '1px solid #e0e0e0'
  },
  reviewCell: {
    padding: '10px 12px',
    fontSize: '13px',
    color: '#455a64',
    borderBottom: '1px solid #f0f0f0'
  },
  emailLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#555',
    marginBottom: '8px'
  },
  cellError: {
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    color: '#c62828',
    fontWeight: 600
  },
  missingValue: {
    color: '#f44336',
    fontStyle: 'italic',
    fontWeight: 600,
    fontSize: '12px'
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(244, 67, 54, 0.07)',
    border: '1px solid rgba(244, 67, 54, 0.3)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  warningIcon: {
    color: '#f44336',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  warningText: {
    fontSize: '13px',
    color: '#b71c1c',
    lineHeight: 1.6
  },
  cellInvalidOrg: {
    backgroundColor: 'rgba(245, 124, 0, 0.1)',
    color: '#e65100',
    fontWeight: 600
  },
  invalidOrgBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(245, 124, 0, 0.07)',
    border: '1px solid rgba(245, 124, 0, 0.4)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  invalidOrgIcon: {
    color: '#f57c00',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  invalidOrgText: {
    fontSize: '13px',
    color: '#4e2600',
    lineHeight: 1.6
  },
  cellDuplicate: {
    backgroundColor: 'rgba(255, 152, 0, 0.12)',
    color: '#e65100',
    fontWeight: 600
  },
  duplicateBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(255, 152, 0, 0.08)',
    border: '1px solid rgba(255, 152, 0, 0.4)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  duplicateIcon: {
    color: '#e65100',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  duplicateText: {
    fontSize: '13px',
    color: '#4e2600',
    lineHeight: 1.6
  },
  duplicateBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 152, 0, 0.2)',
    color: '#e65100',
    fontWeight: 700,
    fontSize: '11px',
    border: '1px solid rgba(255, 152, 0, 0.5)',
    whiteSpace: 'nowrap'
  },
  importInfo: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f5f9ff',
    borderRadius: '8px',
    border: '1px solid rgba(3, 169, 244, 0.2)',
    margin: '0 0 16px'
  },
  infoNumberCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#03a9f4',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
    marginRight: '12px',
    flexShrink: 0
  },
  importInfoText: {
    fontSize: '14px',
    color: '#455a64',
    fontWeight: 500
  }
})

export default ImportOrgRepresentativesModal
